/**
 * Mock API interceptor for development
 * Provides mock responses for endpoints not yet implemented in the backend
 */

import {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { isConfigEnabled } from '@/config/config';

// Type for the interceptor
interface MockInterceptor<T = unknown> {
  method: 'get' | 'post' | 'put' | 'delete';
  url: string | RegExp;
  response: T;
  status?: number;
  delay?: number;
}

// Check if mocking is enabled via configuration
const USE_MOCK_API = isConfigEnabled('REACT_APP_USE_MOCK_API');

// Define mock endpoints
// Empty for now - ready for future mocks
const mockEndpoints: MockInterceptor[] = [
  // Example mock endpoint:
  // {
  //   method: 'get',
  //   url: '/some-endpoint',
  //   response: { data: 'mock data' },
  //   status: 200,
  //   delay: 100 // Simulate network delay
  // }
];

/**
 * Setup mock interceptors for axios instance
 * Only active in development mode and when USE_MOCK_API is true
 */
export function setupMockInterceptors(axiosInstance: AxiosInstance) {
  if (process.env.NODE_ENV !== 'development' || !USE_MOCK_API) {
    return;
  }

  // Add request interceptor to handle mocked endpoints
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const mockEndpoint = mockEndpoints.find((mock) => {
        const urlMatches =
          typeof mock.url === 'string'
            ? config.url?.includes(mock.url)
            : mock.url.test(config.url || '');

        const methodMatches = config.method?.toLowerCase() === mock.method;

        return urlMatches && methodMatches;
      });

      if (mockEndpoint) {
        // Found a mock endpoint
        if (mockEndpoint.delay) {
          await new Promise<void>((resolve) => setTimeout(() => { resolve(); }, mockEndpoint.delay));
        }

        console.log(`🎭 Mocking ${config.method?.toUpperCase()} ${config.url}`);

        // Create a mock adapter response by throwing a special error
        const mockError = new Error('MOCK_RESPONSE') as Error & {
          config: InternalAxiosRequestConfig;
          response: AxiosResponse;
          isAxiosError: boolean;
        };
        mockError.config = config;
        mockError.response = {
          data: mockEndpoint.response,
          status: mockEndpoint.status || 200,
          statusText: 'OK',
          headers: {},
          config: config as AxiosRequestConfig,
        } as AxiosResponse;
        mockError.isAxiosError = true;

        throw mockError;
      }

      return config;
    },
    (error: unknown) => {
      return Promise.reject(error);
    }
  );

  console.log('🎭 Mock API interceptors initialized (REACT_APP_USE_MOCK_API=true)');
  console.log(`📝 ${mockEndpoints.length} mock endpoints registered`);
}

// Helper function to add mock endpoints dynamically (useful for testing)
export function addMockEndpoint<T = unknown>(mock: MockInterceptor<T>) {
  if (USE_MOCK_API) {
    mockEndpoints.push(mock as MockInterceptor);
    console.log(`🎭 Added mock endpoint: ${mock.method.toUpperCase()} ${mock.url}`);
  }
}

// Helper function to clear all mock endpoints
export function clearMockEndpoints() {
  mockEndpoints.length = 0;
  console.log('🎭 Cleared all mock endpoints');
}
